import React, { useState, useEffect } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Product } from "../Types/Types";
import ImageUpload from "../Assets/ImageUpload";
import FormActionButtons from "../Assets/FormActionButtons";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface ProductEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (productId: string, product: FormData) => void;
    initialData: Product;
    category: string;
    lang: string;
    categoryName: string
}

type Multi = { en: string; mk: string; sq: string };

const toMulti = (field: { [key: string]: string } | undefined): Multi => ({
    en: field?.en || "",
    mk: field?.mk || "",
    sq: field?.sq || "",
});

const ProductEditModal: React.FC<ProductEditModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    category,
    categoryName
}) => {
    const { t } = useTranslation();
    const [activeLang, setActiveLang] = useState<FormLang>("en");
    const [name, setName] = useState<Multi>(toMulti(initialData.name));
    const [description, setDescription] = useState<Multi>(toMulti(initialData.description));
    const [dosage, setDosage] = useState<Multi>(toMulti(initialData.dosage));
    const [image, setImage] = useState<File | null>(null);

    useEffect(() => {
        setName(toMulti(initialData.name));
        setDescription(toMulti(initialData.description));
        setDosage(toMulti(initialData.dosage));
    }, [initialData]);

    const setField = (
        setter: React.Dispatch<React.SetStateAction<Multi>>,
        value: string
    ) => setter((prev) => ({ ...prev, [activeLang]: value }));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("name", JSON.stringify(name));
        formData.append("description", JSON.stringify(description));
        formData.append("dosage", JSON.stringify(dosage));
        formData.append("categoryName", category);
        if (image) {
            formData.append("image", image);
        }

        onSubmit(initialData._id, formData);
        onClose();
    };

    if (!isOpen) return null;

    const langSuffix = activeLang.toUpperCase();

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-semibold mb-4 text-center"> {t('forms.editProduct')}  - {categoryName}</h2>

                <LanguageTabs active={activeLang} onChange={setActiveLang} />

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block font-medium mb-1"> {t('forms.name')} ({langSuffix})</label>
                        <input
                            type="text"
                            value={name[activeLang]}
                            onChange={(e) => setField(setName, e.target.value)}
                            className="w-full border p-2 rounded"
                            required={activeLang !== "sq"}
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-1"> {t('forms.description')} ({langSuffix})</label>
                        <ReactQuill
                            key={`desc-${activeLang}`}
                            value={description[activeLang]}
                            onChange={(value) => setField(setDescription, value)}
                            theme="snow"
                            className="h-64"
                        />
                    </div>
                    <br></br>
                    <br></br>

                    <div>
                        <label className="block font-medium mb-1"> {t('forms.dosage')} ({langSuffix})</label>
                        <input
                            type="text"
                            value={dosage[activeLang]}
                            onChange={(e) => setField(setDosage, e.target.value)}
                            className="w-full border p-2 rounded"
                            required={activeLang !== "sq"}
                        />
                    </div>

                    <ImageUpload
                        onImageSelect={(file) => setImage(file)}
                        initialImage={!image ? initialData.image : undefined}
                    />
                    <FormActionButtons
                        edit={true}
                        onClose={onClose}
                        onSubmit={handleSubmit}
                        disabled={false}
                    />
                </form>
            </div>
        </div>
    );
};

export default ProductEditModal;
